import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api, { apiError, fmtMoney, fmtNum } from "@/lib/api";
import { downloadRentBillCsv, downloadRentBillsListCsv, downloadRentStatementCsv } from "@/lib/downloadCsv";
import { printRentBill } from "@/lib/printRentBill";
import { useMasters, opt, optIncludingInactive } from "@/lib/useMasters";
import { PageHeader, Card, Field, SelectInput, TextInput, NumberInput, StatusBadge, CrudActions } from "@/components/kit";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Loader2, Plus, FileText, Calculator, Send, Printer, Download, Eye } from "lucide-react";

export default function Rent() {
  return (
    <div data-testid="rent-page">
      <PageHeader title="Rent Billing" subtitle="Storage-rent contracts with auto-calculated periodic charges posted to party ledgers" />
      <Tabs defaultValue="contracts">
        <TabsList className="mb-4">
          <TabsTrigger value="contracts" data-testid="tab-contracts">Contracts</TabsTrigger>
          <TabsTrigger value="bills" data-testid="tab-bills">Bills</TabsTrigger>
          <TabsTrigger value="statement" data-testid="tab-statement">Statement</TabsTrigger>
        </TabsList>
        <TabsContent value="contracts"><Contracts /></TabsContent>
        <TabsContent value="bills"><Bills /></TabsContent>
        <TabsContent value="statement"><Statement /></TabsContent>
      </Tabs>
    </div>
  );
}

function Contracts() {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const { data = [], isLoading } = useQuery({ queryKey: ["rent-contracts"], queryFn: async () => (await api.get("/finance/rent/contracts")).data });

  const remove = async (c) => {
    if (!window.confirm(`Delete contract ${c.contract_number}? Posted bills will close it instead.`)) return;
    try {
      const { data } = await api.delete(`/finance/rent/contracts/${c.id}`);
      toast.success(data.status === "closed" ? "Contract closed (has posted bills)" : "Contract deleted");
      qc.invalidateQueries({ queryKey: ["rent-contracts"] });
    } catch (e) { toast.error(apiError(e)); }
  };

  const rateLabel = (c) => {
    const rates = c.rates || [];
    if (rates.length > 1) return `${rates.length} bag rates`;
    if (rates.length === 1) return fmtMoney(rates[0].rate_per_bag_month);
    return fmtMoney(c.rate);
  };

  return (
    <div>
      <div className="mb-3 flex justify-end">
        <button data-testid="add-contract-btn" onClick={() => { setEditing(null); setOpen(true); }} className="flex h-10 items-center gap-2 rounded-full bg-emerald-800 px-4 text-sm font-semibold text-white hover:bg-emerald-900">
          <Plus size={16} /> New Contract
        </button>
      </div>
      {isLoading ? <Spin /> : (
        <Card className="overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-stone-50 text-left text-[11px] uppercase text-stone-500">
                <tr><th className="px-4 py-3">Contract</th><th className="px-4 py-3">Party</th><th className="px-4 py-3">Warehouse</th><th className="px-4 py-3">Method</th><th className="px-4 py-3 text-right">Rate</th><th className="px-4 py-3">Status</th><th className="px-4 py-3 text-right">Actions</th></tr>
              </thead>
              <tbody>
                {data.length === 0 && <tr><td colSpan={7} className="px-4 py-8 text-center text-stone-400">No contracts</td></tr>}
                {data.map((c) => (
                  <tr key={c.id} className="border-t border-stone-100 even:bg-stone-50/50">
                    <td className="px-4 py-3 font-mono text-xs font-semibold text-emerald-700">{c.contract_number}</td>
                    <td className="px-4 py-3">{c.party_name}</td>
                    <td className="px-4 py-3">{c.warehouse_name}</td>
                    <td className="px-4 py-3">{c.rent_method}</td>
                    <td className="px-4 py-3 text-right">{rateLabel(c)}</td>
                    <td className="px-4 py-3"><StatusBadge tone={c.status === "Active" ? "emerald" : "neutral"}>{c.status}</StatusBadge></td>
                    <td className="px-4 py-3"><CrudActions testid={`contract-${c.id}`} onEdit={() => { setEditing(c); setOpen(true); }} onDelete={() => remove(c)} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}
      <ContractDialog open={open} record={editing} onClose={() => { setOpen(false); setEditing(null); }} onSaved={() => qc.invalidateQueries({ queryKey: ["rent-contracts"] })} />
    </div>
  );
}

function ContractDialog({ open, onClose, onSaved, record }) {
  const m = useMasters();
  const blank = {
    party_id: "", warehouse_id: "", all_warehouses: true, rent_method: "Per Bag / Month", rate: "",
    min_billing: "", effective_from: new Date().toISOString().slice(0, 10), tax_treatment: "Non-GST", status: "Active",
    rates: {},
  };
  const [f, setF] = useState(blank);
  const [saving, setSaving] = useState(false);
  const { data: methods } = useQuery({ queryKey: ["rent-methods"], queryFn: async () => (await api.get("/finance/rent/methods")).data, enabled: open });
  const activeWhCount = (m.warehouses || []).filter((w) => w.active !== false).length;
  const bagMethods = f.rent_method === "Per Bag / Month" || f.rent_method === "Per Bag / Day";
  const activeBags = (m.bagTypes || []).filter((b) => b.active !== false);

  useEffect(() => {
    if (!open) return;
    if (record) {
      const rateMap = {};
      (record.rates || []).forEach((r) => {
        rateMap[r.bag_type_id] = String(r.rate_per_bag_month ?? "");
      });
      setF({
        party_id: record.party_id || "",
        warehouse_id: record.warehouse_id || "",
        all_warehouses: !!record.all_warehouses,
        rent_method: record.rent_method || "",
        rate: record.rate ?? "", min_billing: record.min_billing ?? "",
        effective_from: (record.effective_from || "").slice(0, 10),
        tax_treatment: record.tax_treatment || "Non-GST", status: record.status || "Active",
        rates: rateMap,
      });
    } else setF(blank);
  }, [open, record]);

  const setBagRate = (bagTypeId, val) => {
    setF((prev) => ({ ...prev, rates: { ...prev.rates, [bagTypeId]: val } }));
  };

  const save = async () => {
    if (!f.party_id || !f.rent_method) return toast.error("Party and rent method are required");
    if (!f.all_warehouses && !f.warehouse_id) return toast.error("Select a warehouse, or apply to all active warehouses");
    const ratesPayload = bagMethods
      ? activeBags
          .map((b) => ({
            bag_type_id: b.id,
            rate_per_bag_month: +f.rates[b.id] || 0,
            rate_per_bag_day: f.rent_method === "Per Bag / Day" ? (+f.rates[b.id] || 0) : ((+f.rates[b.id] || 0) / 30),
          }))
          .filter((r) => r.rate_per_bag_month > 0 || r.rate_per_bag_day > 0)
      : [];
    if (bagMethods && ratesPayload.length === 0 && !(+f.rate > 0)) {
      return toast.error("Enter at least one bag-type rate (or a fallback rate)");
    }
    setSaving(true);
    try {
      const body = {
        party_id: +f.party_id,
        all_warehouses: !!f.all_warehouses,
        warehouse_id: f.all_warehouses ? null : +f.warehouse_id,
        rent_method: f.rent_method,
        rate: +f.rate || (ratesPayload[0]?.rate_per_bag_month || 0),
        min_billing: +f.min_billing || 0,
        effective_from: f.effective_from,
        tax_treatment: f.tax_treatment,
        status: f.status,
        rates: bagMethods ? ratesPayload : [],
      };
      if (record) { await api.put(`/finance/rent/contracts/${record.id}`, body); toast.success("Contract updated"); }
      else { await api.post("/finance/rent/contracts", body); toast.success("Contract created"); }
      onSaved(); onClose();
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="contract-dialog" className="sm:max-w-xl">
        <DialogHeader><DialogTitle className="font-display">{record ? "Edit Rent Contract" : "New Rent Contract"}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Party" required className="col-span-2">
            <SelectInput options={optIncludingInactive(m.parties, (p) => `${p.name} (${p.code})`)} value={f.party_id} onChange={(e) => setF({ ...f, party_id: e.target.value })} data-testid="contract-party" />
          </Field>
          <label className="col-span-2 flex items-start gap-2 rounded-lg border border-stone-200 bg-stone-50 px-3 py-2.5 text-sm">
            <input
              type="checkbox"
              className="mt-0.5"
              checked={!!f.all_warehouses}
              onChange={(e) => setF({ ...f, all_warehouses: e.target.checked, warehouse_id: e.target.checked ? "" : f.warehouse_id })}
              data-testid="contract-all-wh"
            />
            <span>
              <span className="font-medium text-stone-800">Same rate for all active warehouses</span>
              <span className="mt-0.5 block text-xs text-stone-500">
                One contract covers stock in every active warehouse ({activeWhCount}). No need to set the rate again per warehouse.
              </span>
            </span>
          </label>
          {!f.all_warehouses && (
            <Field label="Warehouse" required className="col-span-2">
              <SelectInput options={optIncludingInactive(m.warehouses, (w) => w.name)} value={f.warehouse_id} onChange={(e) => setF({ ...f, warehouse_id: e.target.value })} data-testid="contract-wh" />
            </Field>
          )}
          <Field label="Rent Method" required className="col-span-2"><SelectInput options={(methods?.methods || []).map((x) => ({ value: x, label: x }))} value={f.rent_method} onChange={(e) => setF({ ...f, rent_method: e.target.value })} data-testid="contract-method" /></Field>
          {bagMethods ? (
            <div className="col-span-2 space-y-3 rounded-lg border border-stone-200 p-3">
              <div className="text-xs font-semibold uppercase tracking-wide text-stone-500">
                Rate by bag type (₹ {f.rent_method === "Per Bag / Day" ? "per bag / day" : "per bag / month"})
              </div>
              <p className="text-xs text-stone-500">Daily rent = closing bags × (rate for that lot&apos;s bag type ÷ 30) when monthly.</p>
              {activeBags.length === 0 && <div className="text-sm text-amber-700">No bag types in masters.</div>}
              <div className="space-y-2">
                {activeBags.map((b) => {
                  const label = `${b.name}${b.nominal_capacity ? ` · ${b.nominal_capacity}` : ""}`;
                  return (
                    <div key={b.id} className="grid grid-cols-[minmax(0,1fr)_7.5rem] items-center gap-3">
                      <span className="truncate text-sm text-stone-700" title={label}>{label}</span>
                      <NumberInput
                        value={f.rates[b.id] ?? ""}
                        onChange={(e) => setBagRate(b.id, e.target.value)}
                        data-testid={`contract-rate-bag-${b.id}`}
                      />
                    </div>
                  );
                })}
              </div>
              <Field label="Fallback rate (if lot bag type has no rate)">
                <NumberInput value={f.rate} onChange={(e) => setF({ ...f, rate: e.target.value })} data-testid="contract-rate" />
              </Field>
            </div>
          ) : (
            <Field label="Rate (₹)" required><NumberInput value={f.rate} onChange={(e) => setF({ ...f, rate: e.target.value })} data-testid="contract-rate" /></Field>
          )}
          <Field label="Min Billing (₹)"><NumberInput value={f.min_billing} onChange={(e) => setF({ ...f, min_billing: e.target.value })} /></Field>
          <Field label="Effective From"><TextInput type="date" value={f.effective_from} onChange={(e) => setF({ ...f, effective_from: e.target.value })} /></Field>
          <Field label="Tax Treatment"><SelectInput options={["Non-GST", "Exempt", "Taxable"].map((x) => ({ value: x, label: x }))} value={f.tax_treatment} onChange={(e) => setF({ ...f, tax_treatment: e.target.value })} /></Field>
          {record && <Field label="Status"><SelectInput options={["Active", "Closed"].map((x) => ({ value: x, label: x }))} value={f.status} onChange={(e) => setF({ ...f, status: e.target.value })} /></Field>}
        </div>
        <button onClick={save} disabled={saving} data-testid="save-contract-btn" className="mt-2 flex h-11 w-full items-center justify-center gap-2 rounded-lg bg-emerald-800 font-semibold text-white hover:bg-emerald-900 disabled:opacity-60">
          {saving ? <Loader2 size={18} className="animate-spin" /> : <Plus size={18} />} {record ? "Update Contract" : "Save Contract"}
        </button>
      </DialogContent>
    </Dialog>
  );
}

function Bills() {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [viewBill, setViewBill] = useState(null);
  const [postTarget, setPostTarget] = useState(null);
  const [postingType, setPostingType] = useState("PARTY_SUMMARY");
  const [posting, setPosting] = useState(false);
  const { data = [], isLoading } = useQuery({ queryKey: ["rent-bills"], queryFn: async () => (await api.get("/finance/rent/bills")).data });

  const postBill = async () => {
    if (!postTarget) return;
    setPosting(true);
    try {
      const { data: d } = await api.post(`/finance/rent/bills/${postTarget.id}/post`, { posting_type: postingType });
      toast.success(`${d.bill_number} posted → ledger (${fmtMoney(d.amount)}) · ${d.posting_type}`);
      setPostTarget(null);
      qc.invalidateQueries();
    } catch (e) { toast.error(apiError(e)); } finally { setPosting(false); }
  };
  const remove = async (b) => {
    if (!window.confirm(b.status === "Posted" ? `Reverse and delete posted bill ${b.bill_number}?` : `Delete draft ${b.bill_number}?`)) return;
    try { await api.delete(`/finance/rent/bills/${b.id}`); toast.success("Bill deleted"); qc.invalidateQueries({ queryKey: ["rent-bills"] }); }
    catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div>
      <div className="mb-3 flex justify-end gap-2">
        {data.length > 0 && (
          <button
            type="button"
            data-testid="export-bills-list-btn"
            onClick={() => { downloadRentBillsListCsv(data); toast.success("Bills list exported"); }}
            className="flex h-10 items-center gap-2 rounded-full border border-stone-300 px-4 text-sm font-semibold text-stone-700 hover:bg-stone-50"
          >
            <Download size={16} /> Export List
          </button>
        )}
        <button data-testid="new-bill-btn" onClick={() => { setEditing(null); setOpen(true); }} className="flex h-10 items-center gap-2 rounded-full bg-emerald-800 px-4 text-sm font-semibold text-white hover:bg-emerald-900">
          <Calculator size={16} /> New Bill
        </button>
      </div>
      {isLoading ? <Spin /> : (
        <Card className="overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-stone-50 text-left text-[11px] uppercase text-stone-500">
                <tr><th className="px-4 py-3">Bill</th><th className="px-4 py-3">Party</th><th className="px-4 py-3">Period</th><th className="px-4 py-3">Basis</th><th className="px-4 py-3 text-right">Amount</th><th className="px-4 py-3">Status</th><th className="px-4 py-3"></th></tr>
              </thead>
              <tbody>
                {data.length === 0 && <tr><td colSpan={7} className="px-4 py-8 text-center text-stone-400">No bills</td></tr>}
                {data.map((b) => (
                  <tr key={b.id} className="border-t border-stone-100 even:bg-stone-50/50">
                    <td className="px-4 py-3 font-mono text-xs font-semibold text-emerald-700">{b.bill_number}</td>
                    <td className="px-4 py-3">{b.party_name}</td>
                    <td className="px-4 py-3 text-xs">{b.period_from} → {b.period_to}</td>
                    <td className="px-4 py-3 text-xs text-stone-500">{fmtNum(b.quantity_basis)} · {b.basis_label}</td>
                    <td className="px-4 py-3 text-right font-semibold">{fmtMoney(b.amount)}</td>
                    <td className="px-4 py-3">
                      <StatusBadge tone={b.status === "Posted" ? "emerald" : "warning"}>{b.status}</StatusBadge>
                      {b.posting_type && <div className="mt-0.5 text-[10px] text-stone-400">{b.posting_type}</div>}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          type="button"
                          data-testid={`view-bill-${b.id}`}
                          onClick={() => setViewBill(b)}
                          className="inline-flex items-center gap-1 rounded-full border border-stone-300 px-3 py-1 text-xs font-semibold text-stone-700 hover:bg-stone-50"
                          title="View / Print / Export"
                        >
                          <Eye size={13} /> View
                        </button>
                        {b.status !== "Posted" && (
                          <button data-testid={`post-bill-${b.id}`} onClick={() => { setPostingType("PARTY_SUMMARY"); setPostTarget(b); }} className="inline-flex items-center gap-1 rounded-full bg-emerald-800 px-3 py-1 text-xs font-semibold text-white hover:bg-emerald-900">
                            <Send size={13} /> Post
                          </button>
                        )}
                        <CrudActions
                          testid={`bill-${b.id}`}
                          onEdit={b.status !== "Posted" ? () => { setEditing(b); setOpen(true); } : undefined}
                          onDelete={() => remove(b)}
                        />
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}
      <BillDialog open={open} record={editing} onClose={() => { setOpen(false); setEditing(null); }} onSaved={() => qc.invalidateQueries({ queryKey: ["rent-bills"] })} />
      <BillViewDialog bill={viewBill} onClose={() => setViewBill(null)} />
      <Dialog open={!!postTarget} onOpenChange={(o) => !o && setPostTarget(null)}>
        <DialogContent data-testid="post-bill-dialog">
          <DialogHeader><DialogTitle className="font-display">Post rent to party ledger</DialogTitle></DialogHeader>
          <p className="text-sm text-stone-600">
            {postTarget?.bill_number} · {fmtMoney(postTarget?.amount)} · {postTarget?.period_from} to {postTarget?.period_to}
          </p>
          <Field label="Posting mode">
            <SelectInput
              options={[
                { value: "PARTY_SUMMARY", label: "Party-wise summary (one debit)" },
                { value: "LOT_WISE", label: "Lot-wise (one debit per lot)" },
              ]}
              value={postingType}
              onChange={(e) => setPostingType(e.target.value)}
              data-testid="posting-type"
            />
          </Field>
          <p className="text-xs text-stone-500">Idempotent: the same contract period cannot be posted twice.</p>
          <button onClick={postBill} disabled={posting} data-testid="confirm-post-bill" className="flex h-11 w-full items-center justify-center gap-2 rounded-lg bg-emerald-800 font-semibold text-white hover:bg-emerald-900 disabled:opacity-60">
            {posting ? <Loader2 size={18} className="animate-spin" /> : <Send size={18} />} Confirm Post
          </button>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function BillDialog({ open, onClose, onSaved, record }) {
  const [f, setF] = useState({ contract_id: "", period_from: "", period_to: "" });
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const { data: contracts = [] } = useQuery({ queryKey: ["rent-contracts"], queryFn: async () => (await api.get("/finance/rent/contracts")).data, enabled: open });

  useEffect(() => {
    if (!open) return;
    if (record) setF({ contract_id: record.contract_id || "", period_from: (record.period_from || "").slice(0, 10), period_to: (record.period_to || "").slice(0, 10) });
    else setF({ contract_id: "", period_from: "", period_to: "" });
    setPreview(null);
  }, [open, record]);

  const calc = async () => {
    if (!f.contract_id || !f.period_from || !f.period_to) return toast.error("Select contract and period");
    setBusy(true);
    try { const { data } = await api.post("/finance/rent/calculate", { contract_id: +f.contract_id, period_from: f.period_from, period_to: f.period_to }); setPreview(data); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  const create = async () => {
    setBusy(true);
    try {
      const body = { contract_id: +f.contract_id, period_from: f.period_from, period_to: f.period_to };
      if (record) { await api.put(`/finance/rent/bills/${record.id}`, body); toast.success("Draft bill updated"); }
      else { await api.post("/finance/rent/bills", body); toast.success("Draft bill created"); }
      onSaved(); reset(); onClose();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  const reset = () => { setF({ contract_id: "", period_from: "", period_to: "" }); setPreview(null); };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && (reset(), onClose())}>
      <DialogContent data-testid="bill-dialog">
        <DialogHeader><DialogTitle className="font-display">{record ? "Edit Rent Bill" : "New Rent Bill"}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Contract" required className="col-span-2">
            <SelectInput options={contracts.map((c) => ({ value: c.id, label: `${c.contract_number} · ${c.party_name} · ${c.rent_method}` }))} value={f.contract_id} onChange={(e) => { setF({ ...f, contract_id: e.target.value }); setPreview(null); }} data-testid="bill-contract" />
          </Field>
          <Field label="Period From" required><TextInput type="date" value={f.period_from} onChange={(e) => { setF({ ...f, period_from: e.target.value }); setPreview(null); }} data-testid="bill-from" /></Field>
          <Field label="Period To" required><TextInput type="date" value={f.period_to} onChange={(e) => { setF({ ...f, period_to: e.target.value }); setPreview(null); }} data-testid="bill-to" /></Field>
        </div>
        <button onClick={calc} disabled={busy} data-testid="calc-bill-btn" className="flex h-10 items-center justify-center gap-2 rounded-lg border border-emerald-700 text-emerald-800 font-semibold hover:bg-emerald-50">
          <Calculator size={16} /> Calculate
        </button>
        {preview && (
          <div className="rounded-lg bg-emerald-50 p-4 text-sm space-y-1" data-testid="bill-preview">
            <div className="flex justify-between"><span className="text-stone-600">Bill days in period</span><span>{preview.holding.billable_days ?? preview.holding.days}</span></div>
            {(preview.holding.billable_days != null && preview.holding.billable_days !== preview.holding.days) && (
              <div className="flex justify-between text-xs text-stone-500"><span>Calendar days in range</span><span>{preview.holding.days}</span></div>
            )}
            <div className="flex justify-between"><span className="text-stone-600">Formula</span><span className="text-right text-xs max-w-[60%]">{preview.basis_label}</span></div>
            {(preview.holding?.billed_exclusions || []).length > 0 && (
              <div className="text-xs text-amber-800 bg-amber-50 rounded px-2 py-1">
                Already posted (excluded):{" "}
                {preview.holding.billed_exclusions.map((x, i) => (
                  <span key={i}>{x.bill_number} {x.from}–{x.to}{x.lot_id ? ` lot#${x.lot_id}` : " (all lots)"}{i < preview.holding.billed_exclusions.length - 1 ? "; " : ""}</span>
                ))}
              </div>
            )}
            <div className="flex justify-between border-t border-emerald-200 pt-1 font-bold"><span>Amount</span><span className="text-emerald-800">{fmtMoney(preview.amount)}</span></div>
            {(preview.statement || []).length > 0 && (
              <div className="mt-2 border-t border-emerald-200 pt-2 space-y-2 text-xs">
                {preview.statement.map((r, i) => (
                  <div key={i} className="rounded bg-white/60 px-2 py-1.5">
                    <div className="font-medium">
                      {r.lot_number ? `${r.lot_number}${r.stack_code ? ` (${r.stack_code})` : ""}` : "—"}
                      {r.bag_type_name ? ` · ${r.bag_type_name}` : ""}
                    </div>
                    <div className="text-stone-600">{r.from === r.to ? r.from : `${r.from} to ${r.to}`}</div>
                    <div className="text-stone-700">{r.calculation || `${fmtNum(r.bags_per_day ?? r.bags)} bags/day × ${r.days} days`}</div>
                    <div className="flex justify-between font-semibold text-emerald-900">
                      <span>{fmtMoney(r.daily_rent)}/day</span>
                      <span>{fmtMoney(r.amount)}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
        {preview && (
          <button onClick={create} disabled={busy} data-testid="create-bill-btn" className="flex h-11 items-center justify-center gap-2 rounded-lg bg-emerald-800 font-semibold text-white hover:bg-emerald-900 disabled:opacity-60">
            {busy ? <Loader2 size={18} className="animate-spin" /> : <FileText size={18} />} {record ? "Update Draft Bill" : "Create Draft Bill"}
          </button>
        )}
      </DialogContent>
    </Dialog>
  );
}

const Spin = () => <div className="grid place-items-center py-16"><Loader2 className="animate-spin text-emerald-700" size={28} /></div>;

function RentBillDocument({ data, title = "Storage Rent Bill", billNumber, status }) {
  if (!data) return null;
  return (
    <div data-testid="rent-bill-document">
      <div className="text-center mb-4">
        <div className="font-display text-xl font-bold">SRM Agri Warehouse LLP</div>
        <div className="text-sm text-stone-500">{title}</div>
        {billNumber && <div className="mt-1 font-mono text-sm font-semibold text-emerald-800">{billNumber}</div>}
        {status && (
          <div className="mt-1">
            <StatusBadge tone={status === "Posted" ? "emerald" : "warning"}>{status}</StatusBadge>
          </div>
        )}
      </div>
      <div className="grid grid-cols-2 gap-2 text-sm mb-4">
        <div><span className="text-stone-500">Party</span><div className="font-semibold">{data.party_name}{data.party_code ? ` (${data.party_code})` : ""}</div></div>
        <div><span className="text-stone-500">Warehouse</span><div className="font-semibold">{data.warehouse_name}</div></div>
        <div><span className="text-stone-500">Contract</span><div className="font-mono text-xs">{data.contract_number} · {data.method}</div></div>
        <div><span className="text-stone-500">Period</span><div>{data.period_from} to {data.period_to}</div></div>
        {data.tax_treatment && (
          <div><span className="text-stone-500">Tax</span><div>{data.tax_treatment}</div></div>
        )}
        {data.posting_type && (
          <div><span className="text-stone-500">Posting</span><div>{data.posting_type}</div></div>
        )}
      </div>
      <table className="w-full text-sm">
        <thead className="bg-stone-50 text-left text-[11px] uppercase text-stone-500">
          <tr>
            <th className="px-3 py-2">Period</th>
            <th className="px-3 py-2">Lot / Stack</th>
            <th className="px-3 py-2">Bag type</th>
            <th className="px-3 py-2 text-right">Bags/day</th>
            <th className="px-3 py-2 text-right">Days</th>
            <th className="px-3 py-2 text-right">Rent/day</th>
            <th className="px-3 py-2">Calculation</th>
            <th className="px-3 py-2 text-right">Amount</th>
          </tr>
        </thead>
        <tbody>
          {(data.statement || []).map((r, i) => (
            <tr key={i} className="border-t border-stone-100">
              <td className="px-3 py-2">{r.from === r.to ? r.from : `${r.from} to ${r.to}`}</td>
              <td className="px-3 py-2 font-mono text-xs">
                {r.lot_number || "—"}
                {r.stack_code ? ` (${r.stack_code}${r.stack_active === false ? ", Inactive" : ""})` : ""}
                {r.warehouse_name ? <div className="font-sans text-[10px] text-stone-400">{r.warehouse_name}</div> : null}
              </td>
              <td className="px-3 py-2 text-xs">{r.bag_type_name || "—"}</td>
              <td className="px-3 py-2 text-right font-medium">{fmtNum(r.bags_per_day ?? r.bags)}</td>
              <td className="px-3 py-2 text-right">{r.days}</td>
              <td className="px-3 py-2 text-right">{fmtMoney(r.daily_rent)}</td>
              <td className="px-3 py-2 text-xs text-stone-600">{r.calculation || "—"}</td>
              <td className="px-3 py-2 text-right font-medium">{fmtMoney(r.amount)}</td>
            </tr>
          ))}
          {(!data.statement || !data.statement.length) && (
            <tr><td colSpan={8} className="px-3 py-6 text-center text-stone-400">No line detail · total {fmtMoney(data.amount)}</td></tr>
          )}
        </tbody>
      </table>
      {(data.holding?.billed_exclusions || []).length > 0 && (
        <p className="mt-3 text-xs text-amber-800 bg-amber-50 rounded-lg px-3 py-2">
          <strong>Already posted (excluded):</strong>{" "}
          {data.holding.billed_exclusions.map((x, i) => (
            <span key={i}>{x.bill_number} ({x.from} to {x.to}){i < data.holding.billed_exclusions.length - 1 ? "; " : ""}</span>
          ))}
        </p>
      )}
      <div className="mt-4 flex justify-between border-t border-stone-200 pt-3 font-bold text-base">
        <span>Total</span>
        <span>{fmtMoney(data.amount)}</span>
      </div>
      <p className="mt-2 text-xs text-stone-400">{data.basis_label}. Monthly rates use ×12 ÷365 days/year.</p>
    </div>
  );
}

function BillViewDialog({ bill, onClose }) {
  const { data, isLoading, isError } = useQuery({
    queryKey: ["rent-bill-detail", bill?.id],
    queryFn: async () => (await api.get(`/finance/rent/bills/${bill.id}`)).data,
    enabled: !!bill?.id,
  });

  const printBill = () => {
    if (!data) return;
    const ok = printRentBill(data, {
      billNumber: data.bill?.bill_number || bill?.bill_number,
      status: data.status || bill?.status,
    });
    if (!ok) toast.error("Pop-up blocked — allow pop-ups to print the bill");
  };

  return (
    <Dialog open={!!bill} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="bill-view-dialog" className="sm:max-w-3xl print:max-w-none print:shadow-none print:border-0 print:max-h-none print:overflow-visible">
        <DialogHeader className="print:hidden">
          <DialogTitle className="font-display">{bill?.bill_number || "Rent Bill"}</DialogTitle>
        </DialogHeader>
        {isLoading && <Spin />}
        {isError && <p className="text-sm text-red-600">Could not load bill details.</p>}
        {data && (
          <>
            <div className="print:hidden flex gap-2 mb-2">
              <button
                type="button"
                data-testid="print-bill-btn"
                onClick={printBill}
                className="flex h-9 items-center gap-2 rounded-lg border border-stone-300 px-3 text-sm font-semibold text-stone-700 hover:bg-stone-50"
              >
                <Printer size={15} /> Print
              </button>
              <button
                type="button"
                data-testid="export-bill-btn"
                onClick={() => { downloadRentBillCsv(data); toast.success("Bill exported"); }}
                className="flex h-9 items-center gap-2 rounded-lg border border-stone-300 px-3 text-sm font-semibold text-stone-700 hover:bg-stone-50"
              >
                <Download size={15} /> Export CSV
              </button>
            </div>
            <RentBillDocument
              data={data}
              billNumber={data.bill?.bill_number || bill?.bill_number}
              status={data.status || bill?.status}
            />
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

function Statement() {
  const [f, setF] = useState({ contract_id: "", period_from: "", period_to: "" });
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const { data: contracts = [] } = useQuery({ queryKey: ["rent-contracts"], queryFn: async () => (await api.get("/finance/rent/contracts")).data });

  const load = async () => {
    if (!f.contract_id || !f.period_from || !f.period_to) return toast.error("Select contract and period");
    setBusy(true);
    try {
      const { data: d } = await api.get("/finance/rent/statement", {
        params: { contract_id: f.contract_id, period_from: f.period_from, period_to: f.period_to },
      });
      setData(d);
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  return (
    <div>
      <Card className="p-4 mb-4 print:hidden">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <Field label="Contract" className="md:col-span-2">
            <SelectInput options={contracts.map((c) => ({ value: c.id, label: `${c.contract_number} · ${c.party_name}` }))} value={f.contract_id} onChange={(e) => setF({ ...f, contract_id: e.target.value })} />
          </Field>
          <Field label="From"><TextInput type="date" value={f.period_from} onChange={(e) => setF({ ...f, period_from: e.target.value })} /></Field>
          <Field label="To"><TextInput type="date" value={f.period_to} onChange={(e) => setF({ ...f, period_to: e.target.value })} /></Field>
        </div>
        <div className="mt-3 flex gap-2">
          <button onClick={load} disabled={busy} className="flex h-10 items-center gap-2 rounded-lg bg-emerald-800 px-4 text-sm font-semibold text-white hover:bg-emerald-900">
            {busy ? <Loader2 size={16} className="animate-spin" /> : <Calculator size={16} />} Generate
          </button>
          {data && (
            <>
              <button
                type="button"
                onClick={() => {
                  const ok = printRentBill(data, { title: "Storage Rent Statement" });
                  if (!ok) toast.error("Pop-up blocked — allow pop-ups to print");
                }}
                className="flex h-10 items-center gap-2 rounded-lg border border-stone-300 px-4 text-sm font-semibold text-stone-700 hover:bg-stone-50"
              >
                <Printer size={16} /> Print
              </button>
              <button
                type="button"
                onClick={() => { downloadRentStatementCsv(data); toast.success("Statement exported"); }}
                className="flex h-10 items-center gap-2 rounded-lg border border-stone-300 px-4 text-sm font-semibold text-stone-700 hover:bg-stone-50"
              >
                <Download size={16} /> Export CSV
              </button>
            </>
          )}
        </div>
      </Card>
      {data && (
        <Card className="p-6 print:shadow-none print:border-0" data-testid="rent-statement">
          <RentBillDocument data={data} title="Storage Rent Statement" />
        </Card>
      )}
    </div>
  );
}
